import { useEffect, useRef, useState } from "react";

/**
 * Crop-before-upload.
 *
 * The image never leaves the browser until it is already the right shape and
 * size: drag to reposition, slide to zoom, and the confirm step draws the
 * visible window onto a canvas at the target resolution. The server therefore
 * only ever stores small, pre-cropped rasters — no server-side image
 * processing, no 12MB phone photos in flight.
 */
export const ImageCropDialog = ({
  file,
  aspect,
  targetWidth,
  title,
  round = false,
  onDone,
  onCancel,
}: {
  file: File;
  /** width / height of the crop frame. */
  aspect: number;
  /** Output bitmap width in px; height follows the aspect. */
  targetWidth: number;
  title: string;
  /** Preview the frame as a circle — for logos. */
  round?: boolean;
  onDone: (blob: Blob) => void;
  onCancel: () => void;
}) => {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1); // 1 = exactly covers the frame
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const dragFrom = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null); // prettier-ignore
  const frameRef = useRef<HTMLDivElement>(null);

  // The frame is sized from the dialog width; keep it in state-free CSS.
  const FRAME_W = 460;
  const frameH = FRAME_W / aspect;

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => setImg(el);
    el.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  if (!img) {
    return (
      <div className="fixed inset-0 z-[60] grid place-items-center bg-ink/45">
        <p className="text-sm text-white">Loading image…</p>
      </div>
    );
  }

  // "cover" scale: the smallest scale at which the image fills the frame.
  const cover = Math.max(FRAME_W / img.width, frameH / img.height);
  const scale = cover * zoom;
  const drawW = img.width * scale;
  const drawH = img.height * scale;

  /** Keeps the image covering the frame — no blank edges, ever. */
  const clamp = (o: { x: number; y: number }, w = drawW, h = drawH) => ({
    x: Math.min(0, Math.max(FRAME_W - w, o.x)),
    y: Math.min(0, Math.max(frameH - h, o.y)),
  });

  const clamped = clamp(offset);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragFrom.current = { x: e.clientX, y: e.clientY, ox: clamped.x, oy: clamped.y }; // prettier-ignore
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const from = dragFrom.current;
    if (!from) return;
    setOffset(
      clamp({ x: from.ox + e.clientX - from.x, y: from.oy + e.clientY - from.y }), // prettier-ignore
    );
  };

  const setZoomKeepingCenter = (nextZoom: number) => {
    // Zoom about the frame center, not the top-left corner.
    const nextScale = cover * nextZoom;
    const ratio = nextScale / scale;
    const cx = FRAME_W / 2;
    const cy = frameH / 2;
    setOffset(
      clamp(
        {
          x: cx - (cx - clamped.x) * ratio,
          y: cy - (cy - clamped.y) * ratio,
        },
        img.width * nextScale,
        img.height * nextScale,
      ),
    );
    setZoom(nextZoom);
  };

  const confirm = () => {
    setBusy(true);
    const targetH = Math.round(targetWidth / aspect);
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return onCancel();
    const k = targetWidth / FRAME_W;
    ctx.drawImage(img, clamped.x * k, clamped.y * k, drawW * k, drawH * k);
    // PNG keeps logo transparency; banners are photos — JPEG at 85%.
    const type = round ? "image/png" : "image/jpeg";
    canvas.toBlob((blob) => (blob ? onDone(blob) : onCancel()), type, 0.85);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="animate-fade fixed inset-0 z-[60] flex items-center justify-center bg-ink/55 p-4 backdrop-blur-[3px]"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="animate-rise w-[520px] max-w-full rounded-[20px] bg-surface p-6 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]">
        <h2 className="m-0 mb-1 text-[19px] font-extrabold tracking-[-0.02em]">
          {title}
        </h2>
        <p className="m-0 mb-4 text-[13.5px] text-muted">
          Drag to reposition, slide to zoom.
        </p>

        <div className="flex justify-center">
          <div
            ref={frameRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={() => (dragFrom.current = null)}
            onPointerCancel={() => (dragFrom.current = null)}
            className={`relative max-w-full cursor-grab touch-none overflow-hidden border-2 border-line-bold bg-line active:cursor-grabbing ${
              round ? "rounded-full" : "rounded-[14px]"
            }`}
            style={{ width: FRAME_W, height: frameH }}
          >
            <img
              src={img.src}
              alt=""
              draggable={false}
              className="pointer-events-none absolute max-w-none select-none"
              style={{
                left: clamped.x,
                top: clamped.y,
                width: drawW,
                height: drawH,
              }}
            />
          </div>
        </div>

        <label className="mt-5 flex items-center gap-3 text-[12px] font-semibold text-body">
          Zoom
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoomKeepingCenter(Number(e.target.value))}
            className="flex-1 accent-primary-600"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-[10px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={confirm}
            className="rounded-[10px] bg-primary-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-700 disabled:bg-line-bold"
          >
            {busy ? "Saving…" : "Use image"}
          </button>
        </div>
      </div>
    </div>
  );
};
